import { memo, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { ModalBody, ModalContent, Modal, ModalHeader, ModalFooter } from '@heroui/react';
import { CalendarDate } from '@internationalized/date';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  useUpdateIntakeMutation,
  useGetIntakesQuery,
  StudyFormat,
  StudyMode,
  STUDY_FORMAT_LABELS,
  STUDY_MODE_LABELS,
} from '@/entities/Courses';
import { AppButton } from '@/shared/ui/AppButton';
import { CustomInput, CustomDateRangePicker, CustomSelect } from '@/shared/ui/Fields';
import { scaleInOut } from '@/shared/lib/utils/modalTransitions';
import { useToast } from '@/shared/lib/hooks/useToast';
import { dateToDateValue } from '@/shared/lib/utils/dateAdapters';
import styles from './EditIntakeModal.module.scss';

interface EditIntakeModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  intakeId: number;
  courseId: number;
  onSuccess?: () => void;
}

interface IntakeFormData {
  title: string;
  dateRange: { start: CalendarDate; end: CalendarDate } | null;
  studyFormat: string;
  studyMode: string;
}

const STUDY_FORMAT_ITEMS = Object.values(StudyFormat).map((format) => ({
  key: format,
  label: STUDY_FORMAT_LABELS[format],
}));

const STUDY_MODE_ITEMS = Object.values(StudyMode).map((mode) => ({
  key: mode,
  label: STUDY_MODE_LABELS[mode],
}));

const intakeSchema = yup.object({
  title: yup.string().required('Intake name is required').min(1).max(255),
  dateRange: yup
    .mixed<{ start: CalendarDate; end: CalendarDate }>()
    .nullable()
    .required('Intake duration is required'),
  studyFormat: yup.string().oneOf([...Object.values(StudyFormat), '']).optional(),
  studyMode: yup.string().oneOf([...Object.values(StudyMode), '']).optional(),
}) as yup.ObjectSchema<IntakeFormData>;

export const EditIntakeModal = memo(
  ({ isOpen, onOpenChange, intakeId, courseId, onSuccess }: EditIntakeModalProps) => {
    const { toast } = useToast();
    const [updateIntake, { isLoading }] = useUpdateIntakeMutation();

    const { data: intakesData } = useGetIntakesQuery(
      {
        courseId,
        page: 1,
        pageSize: 1000,
      },
      {
        skip: !isOpen || !intakeId,
      },
    );

    const intake = intakesData?.data?.intakes?.find((i) => i.id === intakeId);

    const {
      control,
      handleSubmit,
      formState: { errors },
      reset,
      setValue,
      trigger,
    } = useForm<IntakeFormData>({
      resolver: yupResolver(intakeSchema) as any,
      defaultValues: {
        title: '',
        dateRange: null,
        studyFormat: '',
        studyMode: '',
      },
    });

    useEffect(() => {
      if (intake && isOpen) {
        reset({
          title: intake.title || '',
          studyFormat: intake.studyFormat || '',
          studyMode: intake.studyMode || '',
        });

        if (intake.startDate && intake.endDate) {
          const startDate = dateToDateValue(new Date(intake.startDate));
          const endDate = dateToDateValue(new Date(intake.endDate));
          if (startDate && endDate) {
            setValue('dateRange', {
              start: startDate,
              end: endDate,
            } as { start: CalendarDate; end: CalendarDate });
          }
        }
      }
    }, [intake, isOpen, reset, setValue]);

    const onSubmit = async (data: IntakeFormData) => {
      if (!data.dateRange || !data.dateRange.start || !data.dateRange.end) {
        toast({
          title: 'Error',
          description: 'Intake duration is required',
          color: 'danger',
        });
        return;
      }

      try {
        const formatDate = (date: CalendarDate): string =>
          `${date.year}-${String(date.month).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`;

        const startDate = formatDate(data.dateRange.start);
        const endDate = formatDate(data.dateRange.end);

        const result = await updateIntake({
          id: intakeId,
          body: {
            title: data.title,
            startDate,
            endDate,
            studyFormat: data.studyFormat || undefined,
            studyMode: data.studyMode || undefined,
          },
        }).unwrap();

        toast({
          title: 'Success',
          description: result.message || 'Intake updated successfully',
          color: 'success',
        });

        handleClose();
        onSuccess?.();
      } catch (error: any) {
        toast({
          title: 'Error',
          description: error?.data?.message || 'Failed to update intake',
          color: 'danger',
        });
      }
    };

    const handleClose = () => {
      reset({
        title: '',
        dateRange: null,
        studyFormat: '',
        studyMode: '',
      });
      onOpenChange(false);
    };

    return (
      <Modal
        isOpen={isOpen}
        onOpenChange={handleClose}
        placement="center"
        motionProps={scaleInOut}
        size="2xl"
        classNames={{
          base: 'p-6 flex flex-col gap-5 max-w-[600px]',
          closeButton: styles.closeBtn,
        }}
      >
        <ModalContent>
          {() => (
            <form onSubmit={handleSubmit(onSubmit as any)}>
              <ModalHeader className={styles.title}>Edit intake</ModalHeader>
              <ModalBody className="p-0 flex flex-col gap-6">
                <Controller
                  name="title"
                  control={control}
                  render={({ field }) => (
                    <CustomInput
                      {...field}
                      label="Intake name"
                      labelPlacement="outside"
                      placeholder="Social Media Strategy in 6 Steps"
                      errorMessage={errors.title?.message}
                      showError={!!errors.title}
                      isRequired
                    />
                  )}
                />

                <Controller
                  name="studyFormat"
                  control={control}
                  render={({ field }) => (
                    <CustomSelect
                      style="black"
                      label="Format"
                      labelPlacement="outside"
                      placeholder="Select format"
                      items={STUDY_FORMAT_ITEMS}
                      selectedKeys={field.value ? new Set([field.value]) : new Set()}
                      onSelectionChange={(keys) => {
                        const selected = Array.from(keys)[0] as string;
                        field.onChange(selected);
                      }}
                      errorMessage={errors.studyFormat?.message}
                      showError={!!errors.studyFormat}
                    />
                  )}
                />

                <Controller
                  name="studyMode"
                  control={control}
                  render={({ field }) => (
                    <CustomSelect
                      style="black"
                      label="Commitment"
                      labelPlacement="outside"
                      placeholder="Select commitment"
                      items={STUDY_MODE_ITEMS}
                      selectedKeys={field.value ? new Set([field.value]) : new Set()}
                      onSelectionChange={(keys) => {
                        const selected = Array.from(keys)[0] as string;
                        field.onChange(selected);
                      }}
                      errorMessage={errors.studyMode?.message}
                      showError={!!errors.studyMode}
                    />
                  )}
                />

                <Controller
                  name="dateRange"
                  control={control}
                  render={({ field }) => (
                    <CustomDateRangePicker
                      label="Intake Duration"
                      labelPlacement="outside"
                      value={field.value}
                      onChange={(value) => {
                        field.onChange(value);
                        trigger('dateRange');
                      }}
                      errorMessage={errors.dateRange?.message || undefined}
                      showError={!!errors.dateRange}
                    />
                  )}
                />
              </ModalBody>
              <ModalFooter className="gap-4 pt-6 px-0">
                <AppButton fullWidth color="white" onPress={handleClose} type="button">
                  Cancel
                </AppButton>
                <AppButton
                  fullWidth
                  color="black"
                  type="submit"
                  isLoading={isLoading}
                  onClick={(e) => {
                    e.preventDefault();
                    handleSubmit(onSubmit as any)();
                  }}
                >
                  Save changes
                </AppButton>
              </ModalFooter>
            </form>
          )}
        </ModalContent>
      </Modal>
    );
  },
);

EditIntakeModal.displayName = 'EditIntakeModal';
